/**
 * Debrief Session Page
 *
 * This page handles the REFLECT phase of the learning loop.
 * It loads the evaluation context and starts a debrief coaching session.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowLeft, MessageSquare, CheckCircle, Target, ArrowRight, Play } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useDebriefTemplate } from '@/hooks/useLearningLoop';

export default function DebriefSessionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { tenant } = useTenant();
  const { toast } = useToast();
  const [profile, setProfile] = useState<any>(null);

  const evaluationId = searchParams.get('evaluation');
  const sessionId = searchParams.get('session');

  const [evaluation, setEvaluation] = useState<any>(null);
  const [agent, setAgent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const { data: debriefTemplate, isLoading: templateLoading } = useDebriefTemplate('sales');

  useEffect(() => {
    async function loadEvaluationContext() {
      if (!evaluationId || !user?.id) {
        setLoading(false);
        return;
      }

      try {
        // Get profile first
        const { data: profileData } = await supabase
          .from('profiles')
          .select('id, company_id')
          .eq('auth_user_id', user.id)
          .maybeSingle();
        
        if (profileData) {
          setProfile(profileData);
        }

        // Fetch the evaluation
        const { data: evalData, error: evalError } = await supabase
          .from('simulation_evaluations')
          .select(`
            *,
            simulation_agents:agent_id (
              id,
              name,
              type,
              description,
              image_url
            )
          `)
          .eq('id', evaluationId)
          .maybeSingle();

        if (evalError) throw evalError;
        if (!evalData) throw new Error('Evaluation not found');

        setEvaluation(evalData);
        setAgent(evalData.simulation_agents);
      } catch (error: any) {
        console.error('Error loading evaluation:', error);
        toast({
          title: 'Error',
          description: 'Failed to load session details',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    }

    loadEvaluationContext();
  }, [evaluationId, user?.id]);

  const handleStartDebrief = async () => {
    if (!debriefTemplate || !evaluation || !profile) return;

    setStarting(true);

    try {
      // Create a new coaching session for the debrief
      const newSessionId = crypto.randomUUID();

      const { error } = await supabase
        .from('coaching_sessions')
        .insert({
          session_id: newSessionId,
          tenant_id: debriefTemplate.tenant_id ?? tenant.tenant_id,
          agent_id: debriefTemplate.id, // Use template ID as agent ID
          template_id: debriefTemplate.id,
          user_id: profile.id,
          auth_user_id: user!.id,
          company_id: user?.company_id ?? null,
          status: 'in_progress',
          is_debrief: true,
          linked_evaluation_id: evaluationId,
          session_start: new Date().toISOString()
        });

      if (error) throw error;

      // Update the evaluation with the debrief session link
      await supabase
        .from('simulation_evaluations')
        .update({ debrief_session_id: newSessionId })
        .eq('id', evaluationId);

      // Store debrief context for the coaching session
      sessionStorage.setItem(`debrief_context_${newSessionId}`, JSON.stringify({
        evaluationId,
        simulationSessionId: sessionId,
        overallScore: evaluation.overall_score,
        strengths: evaluation.detailed_evaluation?.strengths || [],
        improvements: evaluation.detailed_evaluation?.areas_for_improvement || [],
        transcript: evaluation.transcript,
        agentName: agent?.name
      }));

      // Navigate to the coaching session page
      // Note: You may need to adjust this route based on your coaching session page setup
      navigate(`/coaching/session/${newSessionId}?debrief=true`);
    } catch (error: any) {
      console.error('Error starting debrief:', error);
      toast({
        title: 'Error',
        description: 'Failed to start debrief session',
        variant: 'destructive'
      });
    } finally {
      setStarting(false);
    }
  };

  if (loading || templateLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertDescription>
            Session not found. Please go back and try again.
          </AlertDescription>
        </Alert>
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="mt-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  const score = evaluation.overall_score || 0;
  const strengths = evaluation.detailed_evaluation?.strengths || [];
  const improvements = evaluation.detailed_evaluation?.areas_for_improvement || [];

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        className="mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center p-4 bg-purple-100 dark:bg-purple-900/40 rounded-full mb-4">
          <MessageSquare className="w-8 h-8 text-purple-600 dark:text-purple-400" />
        </div>
        <h1 className="text-3xl font-bold mb-2">Debrief Session</h1>
        <p className="text-muted-foreground">
          Reflect on your simulation with your AI coach
        </p>
      </div>

      {/* Learning Loop Progress */}
      <Card className="mb-8 p-4 bg-muted/30">
        <div className="flex items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <div className="font-medium">PREPARE</div>
              <div className="text-xs text-muted-foreground">Coaching</div>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-muted-foreground" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <div className="font-medium">PRACTICE</div>
              <div className="text-xs text-muted-foreground">Score: {score}</div>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-muted-foreground" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center animate-pulse">
              <Play className="w-4 h-4 text-white" />
            </div>
            <div className="text-left">
              <div className="font-medium text-purple-600 dark:text-purple-400">REFLECT</div>
              <div className="text-xs text-muted-foreground">Ready to start</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Session Summary */}
      <Card className="mb-8 p-6">
        <h2 className="text-lg font-semibold mb-4">Session Summary</h2>

        {agent && (
          <div className="flex items-center gap-4 mb-6 pb-6 border-b">
            {agent.image_url ? (
              <img
                src={agent.image_url}
                alt={agent.name}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Target className="w-8 h-8 text-primary" />
              </div>
            )}
            <div>
              <h3 className="font-medium">{agent.name}</h3>
              <p className="text-sm text-muted-foreground capitalize">{agent.type} Simulation</p>
            </div>
            <div className="ml-auto text-right">
              <div className="text-3xl font-bold text-primary">{score}</div>
              <div className="text-sm text-muted-foreground">Overall Score</div>
            </div>
          </div>
        )}

        {/* Key Points */}
        <div className="grid md:grid-cols-2 gap-6">
          {strengths.length > 0 && (
            <div>
              <h4 className="font-medium text-green-600 dark:text-green-400 mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Strengths
              </h4>
              <ul className="space-y-1.5">
                {strengths.slice(0, 3).map((s: string, i: number) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className="text-green-500 mt-0.5">+</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {improvements.length > 0 && (
            <div>
              <h4 className="font-medium text-amber-600 dark:text-amber-400 mb-2 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Areas for Growth
              </h4>
              <ul className="space-y-1.5">
                {improvements.slice(0, 3).map((s: string, i: number) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">-</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Card>

      {/* What to Expect */}
      <Card className="mb-8 p-6 bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800">
        <h3 className="font-semibold text-purple-800 dark:text-purple-300 mb-3">
          What to Expect
        </h3>
        <ul className="space-y-2 text-sm text-purple-700 dark:text-purple-400">
          <li className="flex items-start gap-2">
            <span className="mt-0.5">1.</span>
            <span>Your AI coach will ask reflective questions about the session</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5">2.</span>
            <span>You'll identify what went well and what could be improved</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5">3.</span>
            <span>Together you'll create specific action items for improvement</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-0.5">4.</span>
            <span>You'll receive a personalized debrief summary document</span>
          </li>
        </ul>
      </Card>

      {/* Start Button */}
      <div className="text-center">
        <Button
          size="lg"
          onClick={handleStartDebrief}
          disabled={starting || !debriefTemplate}
          className="bg-purple-600 hover:bg-purple-700 text-white px-8"
        >
          {starting ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Starting...
            </>
          ) : (
            <>
              <MessageSquare className="w-5 h-5 mr-2" />
              Start Debrief Session
            </>
          )}
        </Button>
        <p className="text-sm text-muted-foreground mt-3">
          Takes approximately 5-10 minutes
        </p>
      </div>
    </div>
  );
}
